import { useState, useEffect, useCallback } from 'react';
import { bruneiToday } from '../../../lib/brunei';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PREF_KEY = 'admin_dashboard_location_id';

/**
 * Single hook that owns: chosen date, chosen location, schedule payload,
 * locations list, and refresh state.
 */
export default function useScheduleData() {
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [date, setDate] = useState(bruneiToday());
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load locations once → pick default = saved pref → "Aman Hills" → first
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const res = await fetch(`${API_URL}/api/locations`, { credentials: 'include' });
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.locations || [];
        setLocations(list);

        const saved = localStorage.getItem(PREF_KEY);
        let chosen = null;
        if (saved && list.find((l) => l.id === saved)) {
          chosen = saved;
        } else {
          const aman = list.find((l) => /aman/i.test(l.name));
          chosen = (aman || list[0])?.id || null;
        }
        setLocationId(chosen);
      } catch (e) {
        setError(e.message);
      }
    };
    loadLocations();
  }, []);

  // Persist location choice
  useEffect(() => {
    if (locationId) localStorage.setItem(PREF_KEY, locationId);
  }, [locationId]);

  const fetchSchedule = useCallback(async () => {
    if (!locationId || !date) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/dashboard/schedule?location_id=${locationId}&date=${date}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSchedule(data);
    } catch (e) {
      setError(e.message);
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, [locationId, date]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const goToday = () => setDate(bruneiToday());
  const shiftDay = (delta) => {
    // Date arithmetic on YYYY-MM-DD using UTC to avoid TZ drift
    const [y, m, d] = date.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + delta);
    const next = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(
      2,
      '0'
    )}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    setDate(next);
  };

  return {
    locations,
    locationId,
    setLocationId,
    date,
    setDate,
    goToday,
    shiftDay,
    schedule,
    loading,
    error,
    refresh: fetchSchedule,
  };
}
